/**
 * EmailTemplates.jsx — Wave 6 · Batch 3 · Cluster B Turn 3.
 *
 * Admin UI for the `email_templates` collection. Owners / admins /
 * managers with the `edit_site_settings` permission can edit subject +
 * bodies, preview against sample vars, send a test, and restore
 * defaults.
 *
 * Cluster B Turn 3 changes (NOT a chrome sweep — the file was already
 * fully on DS tokens; this turn adopts admin primitives):
 *   - Filter row wrapped in `_primitives.Section` (`email-templates-
 *     filter-section` testid).
 *   - List table wrapped in `_primitives.Section` (`email-templates-
 *     section` testid).
 *   - Refresh action swapped to `_primitives.GhostButton`.
 *   - `TemplateEditorModal` migrated to modal_convention.md §2 recipe:
 *     warm backdrop `rgba(27,26,22,0.55)`, `var(--ds-radius-card)`
 *     panel, `var(--ds-color-admin-border)` hairline, `var(--ds-shadow-
 *     modal)` shadow, sticky footer with `AccentButton` / `GhostButton`.
 *   - PRR (2026-07): `TemplateEditorModal` now listens for `Escape` and
 *     closes gracefully — matches the DS `Modal` primitive's ESC
 *     contract (see `design-system/hooks/useFocusTrap.js`). The bespoke
 *     tabbed shell intentionally opts out of the full focus-trap
 *     (per `modal_convention.md` §4), so this is the surgical piece
 *     needed to close the WCAG-adjacent gap flagged in the Final
 *     Executive Design Audit §8.4.
 *   - Edit-tab textareas (description rows=2, html_body rows=14,
 *     text_body rows=6) migrated to `_primitives.TextArea`.
 *   - Subject text + Preview/Send-test var inputs + recipient input +
 *     top-of-list search → `_primitives.TextInput`.
 *   - Filter selects (enabled + system) → `_primitives.SelectInput`.
 *   - `AccentButton` on Render-preview / Send-test-submit / Editor-save.
 *   - Restore-default `ConfirmDialog` verified as `variant="destructive"`
 *     (Restore overwrites the template config document); copy byte-exact.
 *
 * BYTE-EXACT PRESERVATIONS (business logic, verified):
 *   - 6 API endpoints: GET /admin/email-templates, GET .../:key,
 *     PATCH .../:key, POST .../:key/restore-default, POST .../:key/
 *     preview, POST .../:key/send-test.
 *   - Form seed shape { description, subject, html_body, text_body,
 *     is_enabled }.
 *   - PATCH payload — same 5 keys.
 *   - Send-test payload { vars, recipient }.
 *   - Send-test validation: `!recipient || !recipient.includes("@")`.
 *   - `template_key` + `event_trigger` immutable (disabled inputs).
 *   - `is_enabled` checkbox semantic + toggle logic.
 *   - Missing-vars banner conditional `result?.missing_vars?.length > 0`.
 *   - TABS array keys (edit / preview / send / vars).
 *   - All existing `email-templates-*` testids.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Eye, RefreshCw, Save, Send, ShieldCheck, ToggleLeft, ToggleRight, Undo2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";

import { useAdminPermissions } from "@/admin/PermissionsContext";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import {
  Section,
  TextInput,
  TextArea,
  SelectInput,
  GhostButton,
  AccentButton,
} from "@/admin/_primitives";

// V1.1b — Admin UI for the email_templates collection.
// Owners / admins / managers (edit_site_settings) can edit subject + bodies,
// preview against sample vars, send a test, restore defaults.
export default function AdminEmailTemplates() {
  const { has } = useAdminPermissions();
  const canEdit = has("edit_site_settings");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [query, setQuery] = useState("");
  const [filterEnabled, setFilterEnabled] = useState("all"); // all | enabled | disabled
  const [filterSystem, setFilterSystem] = useState("all");   // all | system | custom

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    let list = [...items];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((it) =>
        (it.name || "").toLowerCase().includes(q) ||
        (it.template_key || "").toLowerCase().includes(q) ||
        (it.event_trigger || "").toLowerCase().includes(q)
      );
    }
    if (filterEnabled !== "all") {
      list = list.filter((it) => (filterEnabled === "enabled" ? it.is_enabled : !it.is_enabled));
    }
    if (filterSystem !== "all") {
      list = list.filter((it) => (filterSystem === "system" ? it.is_system : !it.is_system));
    }
    return list;
  }, [items, query, filterEnabled, filterSystem]);

  return (
    <AdminLayout
      title="Email Templates"
      subtitle="Edit, preview, and send-test every transactional email — no code deploys."
      actions={
        <GhostButton data-testid="email-templates-refresh" onClick={reload}>
          <RefreshCw className="w-3 h-3" /> Refresh
        </GhostButton>
      }
    >
      <div className="mb-4 max-w-3xl">
        <AdminPageHelp
          title="Email Templates"
          purpose="Manage the copy and structure of every transactional email the platform sends."
          configures="Subject lines, body copy, template variables. Preview and restore to defaults per template."
          impact="Live users receive the edited copy immediately on next send. Delivery format is unchanged."
          safety="Every template supports Restore Default — reverts to the seeded copy shipped with the app. Restoring is destructive to unsaved edits."
        />
      </div>

      {/* Filters */}
      <Section title="Filter" testid="email-templates-filter-section">
        <div className="flex flex-wrap items-center gap-3">
          <TextInput
            data-testid="email-templates-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name / key / event…"
            className="flex-1 min-w-[220px]"
          />
          <SelectInput
            data-testid="email-templates-filter-enabled"
            value={filterEnabled}
            onChange={(e) => setFilterEnabled(e.target.value)}
          >
            <option value="all">All status</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </SelectInput>
          <SelectInput
            data-testid="email-templates-filter-system"
            value={filterSystem}
            onChange={(e) => setFilterSystem(e.target.value)}
          >
            <option value="all">All types</option>
            <option value="system">System</option>
            <option value="custom">Custom</option>
          </SelectInput>
        </div>
      </Section>

      {/* Table */}
      <Section title={`Templates${loading ? "" : ` (${filtered.length})`}`} testid="email-templates-section" bodyClassName="p-0">
        <div className="overflow-x-auto" data-testid="email-templates-table">
          <table className="w-full text-[13px]">
            <thead style={{ background: "var(--ds-color-admin-surface-muted)" }}>
              <tr className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ds-color-ink-muted)" }}>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Event trigger</th>
                <th className="text-left px-4 py-2 hidden lg:table-cell">Template key</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2 hidden md:table-cell tabular-nums">v</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>No templates match these filters.</td></tr>
              ) : filtered.map((it) => (
                <tr
                  key={it.template_key}
                  data-testid={`email-templates-row-${it.template_key}`}
                  className="transition-colors"
                  style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium" style={{ color: "var(--ds-color-ink)" }}>{it.name}</div>
                    {it.is_system && (
                      <span
                        data-testid={`email-templates-system-${it.template_key}`}
                        className="inline-flex items-center gap-1 mt-0.5 text-[10px] uppercase tracking-[0.18em]"
                        style={{ color: "var(--ds-color-accent)" }}
                      >
                        <ShieldCheck className="w-3 h-3" /> System
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 hidden md:table-cell" style={{ color: "var(--ds-color-ink-muted)" }}>
                    <code className="text-xs">{it.event_trigger}</code>
                  </td>
                  <td className="px-4 py-2 hidden lg:table-cell" style={{ color: "var(--ds-color-ink-muted)" }}>
                    <code className="text-xs">{it.template_key}</code>
                  </td>
                  <td className="px-4 py-2">
                    {it.is_enabled ? (
                      <span
                        data-testid={`email-templates-enabled-${it.template_key}`}
                        className="inline-flex items-center gap-1 text-xs"
                        style={{ color: "var(--ds-color-success)" }}
                      >
                        <ToggleRight className="w-4 h-4" /> Enabled
                      </span>
                    ) : (
                      <span
                        data-testid={`email-templates-disabled-${it.template_key}`}
                        className="inline-flex items-center gap-1 text-xs"
                        style={{ color: "var(--ds-color-warning)" }}
                      >
                        <ToggleLeft className="w-4 h-4" /> Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 hidden md:table-cell text-xs tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>v{it.version}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      data-testid={`email-templates-edit-${it.template_key}`}
                      onClick={() => setActiveKey(it.template_key)}
                      className="text-xs hover:underline transition-colors"
                      style={{ color: "var(--ds-color-accent)" }}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {activeKey && (
        <TemplateEditorModal
          templateKey={activeKey}
          canEdit={canEdit}
          onClose={() => setActiveKey(null)}
          onSaved={reload}
        />
      )}
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Editor modal — tabs: Edit / Preview / Send test / Variables
// ---------------------------------------------------------------------------

const TABS = [
  { key: "edit", label: "Edit" },
  { key: "preview", label: "Preview" },
  { key: "send", label: "Send test" },
  { key: "vars", label: "Variables" },
];

function TemplateEditorModal({ templateKey, canEdit, onClose, onSaved }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("edit");
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);

  // A11y — Escape closes the modal, matching the DS `Modal` primitive's
  // ESC pattern (see `design-system/hooks/useFocusTrap.js`). The bespoke
  // tabbed-editor shell intentionally opts out of the full focus-trap
  // (per `modal_convention.md` §4), but Escape-to-dismiss is table-stakes.
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/email-templates/${templateKey}`);
      setOriginal(data);
      setForm({
        description: data.description || "",
        subject: data.subject || "",
        html_body: data.html_body || "",
        text_body: data.text_body || "",
        is_enabled: !!data.is_enabled,
      });
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    }
  }, [templateKey, onClose]);

  useEffect(() => { reload(); }, [reload]);

  const dirty = useMemo(() => {
    if (!original || !form) return false;
    return (
      form.description !== (original.description || "") ||
      form.subject !== (original.subject || "") ||
      form.html_body !== (original.html_body || "") ||
      form.text_body !== (original.text_body || "") ||
      form.is_enabled !== !!original.is_enabled
    );
  }, [original, form]);

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/email-templates/${templateKey}`, form);
      setOriginal(data);
      toast.success("Template saved.");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore() {
    if (!canEdit) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${templateKey}/restore-default`);
      setOriginal(data);
      setForm({
        description: data.description || "",
        subject: data.subject || "",
        html_body: data.html_body || "",
        text_body: data.text_body || "",
        is_enabled: !!data.is_enabled,
      });
      toast.success("Restored to default.");
      setConfirmingReset(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRestoring(false);
    }
  }

  if (!form || !original) {
    return null;
  }

  return (
    <div
      data-testid="email-templates-editor-modal"
      className="fixed inset-0 z-50 flex items-stretch justify-center"
      style={{ background: "rgba(27,26,22,0.55)" }}
    >
      <div
        className="my-8 mx-4 w-full max-w-5xl rounded-[var(--ds-radius-card)] flex flex-col overflow-hidden"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "var(--ds-shadow-modal)",
        }}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-5 py-4 z-10"
          style={{
            background: "var(--ds-color-surface)",
            borderBottom: "1px solid var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="email-templates-editor-back"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-xs transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ds-color-accent)" }}>Email template</div>
              <div className="font-display text-lg truncate" style={{ color: "var(--ds-color-ink)" }}>{original.name}</div>
            </div>
            {original.is_system && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ds-color-accent)" }}>
                <ShieldCheck className="w-3 h-3" /> System
              </span>
            )}
          </div>
          <div className="text-xs tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>v{original.version}</div>
        </header>

        {/* Tabs */}
        <div className="flex gap-1 px-5" style={{ borderBottom: "1px solid var(--ds-color-admin-border)" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`email-templates-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-4 py-3 text-sm transition-colors"
              style={{
                color: tab === t.key ? "var(--ds-color-ink)" : "var(--ds-color-ink-muted)",
                borderBottom: tab === t.key ? "2px solid var(--ds-color-accent)" : "2px solid transparent",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {tab === "edit" && (
            <EditTab
              form={form}
              original={original}
              canEdit={canEdit}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          )}
          {tab === "preview" && <PreviewTab templateKey={templateKey} required_vars={original.required_vars || []} />}
          {tab === "send" && <SendTestTab templateKey={templateKey} required_vars={original.required_vars || []} defaultRecipient={user?.email} disabled={!original.is_enabled} />}
          {tab === "vars" && <VarsTab required_vars={original.required_vars || []} />}
        </div>

        {/* Sticky footer for Edit tab */}
        {tab === "edit" && (
          <div
            className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap"
            style={{
              borderTop: "1px solid var(--ds-color-admin-border)",
              background: "var(--ds-color-admin-surface-muted)",
            }}
          >
            <GhostButton
              data-testid="email-templates-restore-default"
              onClick={() => setConfirmingReset(true)}
              disabled={!canEdit || restoring}
            >
              <Undo2 className="w-3 h-3" /> Restore default
            </GhostButton>
            <div className="flex items-center gap-2">
              <GhostButton
                data-testid="email-templates-cancel"
                onClick={onClose}
              >
                Close
              </GhostButton>
              <AccentButton
                data-testid="email-templates-save"
                onClick={handleSave}
                disabled={!canEdit || !dirty || saving}
              >
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
              </AccentButton>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingReset}
        onCancel={() => setConfirmingReset(false)}
        onConfirm={handleRestore}
        busy={restoring}
        variant="destructive"
        title="Reset template to seeded defaults?"
        description="Your custom subject / HTML / text body will be replaced with the platform-supplied original. Recipients who have already received the current template are unaffected."
        confirmLabel="Reset template"
        testId="email-templates-restore-confirm"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function EditTab({ form, original, canEdit, onChange }) {
  return (
    <div className="space-y-4">
      <FieldRow label="Template key (immutable)">
        <TextInput value={original.template_key} disabled className="w-full" />
      </FieldRow>
      <FieldRow label="Event trigger (immutable)">
        <TextInput value={original.event_trigger} disabled className="w-full" />
      </FieldRow>
      <FieldRow label="Description">
        <TextArea
          value={form.description}
          data-testid="email-templates-field-description"
          disabled={!canEdit}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
          className="w-full"
        />
      </FieldRow>
      <FieldRow label="Subject (Jinja2)">
        <TextInput
          value={form.subject}
          data-testid="email-templates-field-subject"
          disabled={!canEdit}
          onChange={(e) => onChange({ subject: e.target.value })}
          className="w-full font-mono"
        />
      </FieldRow>
      <FieldRow label="HTML body (Jinja2)">
        <TextArea
          value={form.html_body}
          data-testid="email-templates-field-html"
          disabled={!canEdit}
          onChange={(e) => onChange({ html_body: e.target.value })}
          rows={14}
          className="w-full text-xs font-mono"
        />
      </FieldRow>
      <FieldRow label="Plain-text body (Jinja2)">
        <TextArea
          value={form.text_body}
          data-testid="email-templates-field-text"
          disabled={!canEdit}
          onChange={(e) => onChange({ text_body: e.target.value })}
          rows={6}
          className="w-full text-xs font-mono"
        />
      </FieldRow>
      <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
        <input
          type="checkbox"
          data-testid="email-templates-field-enabled"
          checked={form.is_enabled}
          disabled={!canEdit}
          onChange={(e) => onChange({ is_enabled: e.target.checked })}
          className="w-4 h-4"
          style={{ accentColor: "var(--ds-color-accent)" }}
        />
        <span style={{ color: "var(--ds-color-ink)" }}>
          Enabled — when off, sends are skipped and logged as{" "}
          <code className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>status=disabled</code>.
        </span>
      </label>
    </div>
  );
}

function PreviewTab({ templateKey, required_vars }) {
  const [vars, setVars] = useState(() => Object.fromEntries((required_vars || []).map((k) => [k, ""])));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function run() {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${templateKey}/preview`, { vars });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  // Auto-run once with empty vars so missing_vars list populates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [templateKey]);

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="space-y-3">
        <FieldSectionLabel>Variables</FieldSectionLabel>
        {(required_vars || []).map((k) => (
          <label key={k} className="block">
            <div className="text-xs mb-1" style={{ color: "var(--ds-color-ink-muted)" }}><code>{k}</code></div>
            <TextInput
              data-testid={`email-templates-preview-var-${k}`}
              value={vars[k] || ""}
              onChange={(e) => setVars((v) => ({ ...v, [k]: e.target.value }))}
              className="w-full"
            />
          </label>
        ))}
        <AccentButton
          data-testid="email-templates-preview-run"
          onClick={run}
          disabled={loading}
        >
          <Eye className="w-3.5 h-3.5" /> {loading ? "Rendering…" : "Render preview"}
        </AccentButton>
      </div>
      <div className="space-y-3">
        <FieldSectionLabel>Rendered</FieldSectionLabel>
        {result?.missing_vars?.length > 0 && (
          <div
            data-testid="email-templates-preview-missing"
            className="text-xs rounded-[var(--ds-radius-input)] px-3 py-2"
            style={{
              background: "var(--ds-color-warning-soft)",
              border: "1px solid var(--ds-color-warning-soft)",
              color: "var(--ds-color-warning)",
            }}
          >
            Missing required vars: {result.missing_vars.join(", ")}
          </div>
        )}
        <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>Subject</div>
        <div
          data-testid="email-templates-preview-subject"
          className="rounded-[var(--ds-radius-input)] px-3 py-2 text-sm font-mono"
          style={{
            background: "var(--ds-color-admin-surface-muted)",
            border: "1px solid var(--ds-color-admin-border)",
            color: "var(--ds-color-ink)",
          }}
        >
          {result?.subject || "—"}
        </div>
        <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>HTML</div>
        <iframe
          data-testid="email-templates-preview-iframe"
          title="Email preview"
          srcDoc={result?.html_body || ""}
          className="w-full h-[420px] rounded-[var(--ds-radius-input)]"
          style={{ background: "white", border: "1px solid var(--ds-color-admin-border)" }}
        />
        <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>Plain text</div>
        <pre
          data-testid="email-templates-preview-text"
          className="rounded-[var(--ds-radius-input)] px-3 py-2 text-xs whitespace-pre-wrap font-mono"
          style={{
            background: "var(--ds-color-admin-surface-muted)",
            border: "1px solid var(--ds-color-admin-border)",
            color: "var(--ds-color-ink)",
          }}
        >
          {result?.text_body || ""}
        </pre>
      </div>
    </div>
  );
}

function SendTestTab({ templateKey, required_vars, defaultRecipient, disabled }) {
  const [vars, setVars] = useState(() => Object.fromEntries((required_vars || []).map((k) => [k, ""])));
  const [recipient, setRecipient] = useState(defaultRecipient || "");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  async function send() {
    if (!recipient || !recipient.includes("@")) {
      toast.error("Enter a valid recipient email first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${templateKey}/send-test`, { vars, recipient });
      setLast(data);
      toast.success(`Send result: ${data.status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 max-w-xl">
      {disabled && (
        <div
          className="text-xs rounded-[var(--ds-radius-input)] px-3 py-2"
          style={{
            background: "var(--ds-color-warning-soft)",
            border: "1px solid var(--ds-color-warning-soft)",
            color: "var(--ds-color-warning)",
          }}
        >
          This template is currently disabled. Re-enable it on the Edit tab before sending a test.
        </div>
      )}
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--ds-color-ink-muted)" }}>Recipient</div>
        <TextInput
          value={recipient}
          data-testid="email-templates-send-recipient"
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="you@your-domain.com"
          className="w-full"
        />
      </label>
      {(required_vars || []).map((k) => (
        <label key={k} className="block">
          <div className="text-xs mb-1" style={{ color: "var(--ds-color-ink-muted)" }}><code>{k}</code></div>
          <TextInput
            data-testid={`email-templates-send-var-${k}`}
            value={vars[k] || ""}
            onChange={(e) => setVars((v) => ({ ...v, [k]: e.target.value }))}
            className="w-full"
          />
        </label>
      ))}
      <AccentButton
        data-testid="email-templates-send-button"
        onClick={send}
        disabled={busy || disabled}
      >
        <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send test"}
      </AccentButton>
      {last && (
        <div
          data-testid="email-templates-send-result"
          className="text-xs rounded-[var(--ds-radius-input)] px-3 py-2"
          style={{
            background: "var(--ds-color-accent-soft)",
            border: "1px solid var(--ds-color-accent-soft)",
            color: "var(--ds-color-ink-muted)",
          }}
        >
          Sent — status: <strong>{last.status}</strong>. Subject: <em>{last.subject}</em>
        </div>
      )}
    </div>
  );
}

function VarsTab({ required_vars }) {
  return (
    <div className="space-y-3 max-w-xl">
      <FieldSectionLabel>Required variables</FieldSectionLabel>
      {(required_vars || []).length === 0 ? (
        <p
          data-testid="email-templates-vars-empty"
          className="text-sm italic"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          This template requires no variables.
        </p>
      ) : (
        <ul className="space-y-2">
          {required_vars.map((k) => (
            <li key={k} className="flex items-start gap-3 text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>
              <code
                className="text-xs px-2 py-1 rounded"
                style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
              >
                {k}
              </code>
              <span>Provided at send time.</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Local helpers — LOCAL, not lifted per Field-portability rule       */
/* ------------------------------------------------------------------ */

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <div
        className="text-[10px] uppercase tracking-[0.18em] mb-1"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function FieldSectionLabel({ children }) {
  return (
    <div
      className="text-[10px] uppercase tracking-[0.18em]"
      style={{ color: "var(--ds-color-ink-muted)" }}
    >
      {children}
    </div>
  );
}
